// noinspection DuplicatedCode

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Colors } from "../../../assets/Colors";
import { fetchGet, fetchPost } from "../../../fetchers";
import { useMutation } from "@tanstack/react-query";
import { Card } from "@material-tailwind/react";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SearchBarSmallV2 } from "../../../components/form/SearchBar";

const PAGE_SIZE = 10;

const PengeluaranBarangPage = () => {
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage]               = useState(1);
  const [totalPage, setTotalPage]     = useState(1);

  const mutationData = useMutation(() => fetchGet("Procurement/GoodsExpenditure/all/1"));
  const KirimTrans   = useMutation((body) => fetchPost("Procurement/GoodsExpenditure/approval", body));

  const header = [
    { name: "ID",           key: "GoodsExpenditureID" },
    { name: "Tanggal",      key: "Tanggal" },
    { name: "Nama Peminta", key: "NamaPeminta" },
    { name: "Departemen",   key: "Departement" },
    { name: "Keterangan",   key: "Keterangan" },
    { name: "Status",       key: "Status" },
  ];

  // ── Load on mount & after kirim ──────────────────────────────
  useEffect(() => { mutationData.mutate(); }, []);
  useEffect(() => {
    if (KirimTrans.isSuccess) mutationData.mutate();
  }, [KirimTrans.status]);

  // ── Client-side filter ────────────────────────────────────────
  const allRows = mutationData.data?.data || [];

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return allRows;
    const kw = searchQuery.toLowerCase();
    return allRows.filter((r) =>
      [r.GoodsExpenditureID, r.NamaPeminta, r.Keterangan, r.Departement]
        .some((v) => v && v.toLowerCase().includes(kw))
    );
  }, [allRows, searchQuery]);

  // ── Pagination ────────────────────────────────────────────────
  useEffect(() => {
    setTotalPage(Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)));
    setPage(1);
  }, [filtered.length]);

  const safePage = Math.min(page, totalPage);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  // ── Search ────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = typeof e === "string" ? e : e?.target?.value || "";
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(val);
      setPage(1);
    }, 300);
  };

  // ── Pagination ────────────────────────────────────────────────
  const prevPage = () => { if (page > 1) setPage((p) => p - 1); };
  const nextPage = () => { if (page < totalPage) setPage((p) => p + 1); };

  // ── Export Excel ──────────────────────────────────────────────
  function handleDownloadExcel() {
    const names = header.map((h) => h.name);
    const body  = filtered.map((row) =>
      header.map((h) =>
        h.key === "Tanggal" && row[h.key]
          ? new Date(row[h.key]).toLocaleDateString("ID")
          : row[h.key] || ""
      )
    );
    downloadExcel({ fileName: "Pengeluaran_Barang", sheet: "data", tablePayload: { header: names, body } });
  }

  // ── Export PDF ────────────────────────────────────────────────
  function handleDownloadPDF() {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(12);
    doc.text("Laporan Pengeluaran Barang", 14, 14);
    autoTable(doc, {
      head: [header.map((h) => h.name)],
      body: filtered.map((row) =>
        header.map((h) =>
          h.key === "Tanggal" && row[h.key]
            ? new Date(row[h.key]).toLocaleDateString("ID")
            : row[h.key] || ""
        )
      ),
      startY: 20,
      styles: { fontSize: 8 },
    });
    doc.save("Pengeluaran_Barang.pdf");
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
        <div className="container mx-auto max-w-full" />
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              <div className="flex flex-col content-center">

                {/* ── Toolbar: Tambah + Download + Search ── */}
                <div className="flex w-full justify-between px-4 pt-4 flex-col sm:flex-row mt-2">
                  <div className="flex flex-row gap-2 flex-wrap">
                    <button
                      onClick={() => navigate("/gudang/transaction/form-pengeluaran-barang")}
                      className="flex flex-row items-center justify-center my-2 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow"
                    >
                      Tambah +
                    </button>
                    <button
                      onClick={handleDownloadExcel}
                      disabled={filtered.length === 0}
                      className="flex flex-row items-center justify-center my-2 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow disabled:opacity-40"
                    >
                      Download Excel
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      disabled={filtered.length === 0}
                      className="flex flex-row items-center justify-center my-2 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-red-400 hover:bg-red-400 hover:text-white rounded shadow disabled:opacity-40"
                    >
                      Download PDF
                    </button>
                  </div>
                  <div className="flex flex-row w-96">
                    <SearchBarSmallV2
                      text={"Cari Nama"}
                      value={searchInput}
                      funcs={handleSearchChange}
                    />
                  </div>
                </div>

                {/* Info filter */}
                {searchQuery && (
                  <div className="px-4 pt-1 text-xs text-green-600">
                    Menampilkan {filtered.length} dari {allRows.length} data untuk "{searchQuery}"
                  </div>
                )}

                {/* ── Table ── */}
                <div className="overflow-x-auto px-4 pt-2">
                  <table className="table-auto w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
                    <thead className="text-xs text-orange-600 uppercase bg-gray-50 dark:bg-gray-700 dark:text-orange-400">
                      <tr>
                        {header.map((h, i) => (
                          <th key={i} scope="col" className="py-3 px-6">{h.name}</th>
                        ))}
                        <th scope="col" className="py-3 px-6">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mutationData.isLoading ? (
                        <tr>
                          <td colSpan={header.length + 1} className="text-center py-8 text-gray-400">
                            Loading...
                          </td>
                        </tr>
                      ) : pageRows.length === 0 ? (
                        <tr>
                          <td colSpan={header.length + 1} className="text-center py-8 text-gray-400">
                            {searchQuery ? `Tidak ada data untuk "${searchQuery}"` : "Tidak ada data"}
                          </td>
                        </tr>
                      ) : (
                        pageRows.map((row, id) => (
                          <tr
                            key={row.GoodsExpenditureID || id}
                            className={id % 2 === 0
                              ? "bg-gray-50 border-b hover:bg-gray-200 hover:text-main-100"
                              : "bg-white border-b hover:bg-gray-200 hover:text-main-100"}
                          >
                            {header.map((h, i) => (
                              <td key={i} className="py-4 px-6">
                                {h.key === "Tanggal" && row[h.key]
                                  ? new Date(row[h.key]).toLocaleDateString("ID")
                                  : h.key === "Status"
                                  ? (
                                    <span className={
                                      row[h.key] === "Selesai" || row[h.key] === "Approved"
                                        ? "text-green-600 font-medium"
                                        : row[h.key] === "Menunggu Approval"
                                        ? "text-yellow-600 font-medium"
                                        : "text-gray-600"
                                    }>
                                      {row[h.key] || "-"}
                                    </span>
                                  )
                                  : row[h.key] || "-"}
                              </td>
                            ))}
                            <td className="py-4 px-6">
                              {row.Status !== "Menunggu Approval" && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => navigate(
                                      "/gudang/transaction/form-pengeluaran-barang/" +
                                      row.BisnisUnitID + "/" +
                                      row.GoodsExpenditureID.replaceAll("/", "-")
                                    )}
                                    className="border border-main-100 text-main-100 hover:bg-main-100 hover:text-white text-xs font-medium rounded-lg px-3 py-1.5"
                                  >
                                    Edit
                                  </button>
                                  {row.Status === "Menunggu Dikirim" && (
                                    <button
                                      onClick={() => KirimTrans.mutate({ GoodsExpenditureID: row.GoodsExpenditureID })}
                                      disabled={KirimTrans.isLoading}
                                      className="border border-main-100 text-main-100 hover:bg-main-100 hover:text-white text-xs font-medium rounded-lg px-3 py-1.5 disabled:opacity-40"
                                    >
                                      Kirim
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ── Pagination: Prev / Page X/Y / Next ── */}
                <div className="flex flex-row justify-end items-center gap-4 px-4 py-4">
                  <button
                    onClick={prevPage}
                    disabled={safePage <= 1}
                    className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                  >
                    Prev
                  </button>
                  <span className="text-sm">
                    Page {safePage} / {totalPage}
                  </span>
                  <button
                    onClick={nextPage}
                    disabled={safePage >= totalPage}
                    className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>

              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PengeluaranBarangPage;
